import type { Department } from "../contexts/AuthProvider";
import { ALLOW_AVAILABILITY_FALLBACK, byDimensionsOf, resolveLocationCensus } from "./censusResolution";
import { getDemoStaffingRules } from "./demoStaffingRules";
import { type DemoRequiredInput, type NodeSplitProblem, scoreByShiftTypeForDay } from "./hospitalDemoScore";
import { type Day, type DepartmentLocation, NurseFullRole, type StatsMode, type WeekData } from "./models";
import { getCensus } from "./utilFunctions";

/** One room (a location name) that a shift's node is split across. */
export type NodeRoom = { name: string; census: number; hasLpnRow: boolean };

/** One room's share of a shift's node. */
export type RoomSplit = { name: string; rn: number; lpn: number };

const byName = (a: { name: string }, b: { name: string }): number => a.name.localeCompare(b.name, "en", { numeric: true });

// Larger census first, then name. Used for every tie in the split.
const byCensusThenName = (a: NodeRoom, b: NodeRoom): number => b.census - a.census || byName(a, b);

/**
 * Split `total` across rooms by census share, using largest remainder so the parts add up to
 * `total`. With no census at all, split evenly and give the rest to the first rooms by name.
 * Returns one share per room, in the same order as `rooms`.
 */
const roomShares = (total: number, rooms: readonly NodeRoom[]): number[] => {
	const totalCensus = rooms.reduce((sum, room) => sum + room.census, 0);
	const indexes = rooms.map((_, index) => index);
	if (totalCensus === 0) {
		const base = Math.floor(total / rooms.length);
		const nameOrder = indexes.sort((a, b) => byName(rooms[a], rooms[b]));
		const extraFor = new Set(nameOrder.slice(0, total - base * rooms.length));
		return rooms.map((_, index) => base + (extraFor.has(index) ? 1 : 0));
	}
	const exact = rooms.map((room) => (total * room.census) / totalCensus);
	const shares = exact.map(Math.floor);
	const remainder = (index: number): number => exact[index] - shares[index];
	const leftover = total - shares.reduce((sum, share) => sum + share, 0);
	const remainderOrder = indexes.sort((a, b) => remainder(b) - remainder(a) || byCensusThenName(rooms[a], rooms[b]));
	for (const index of remainderOrder.slice(0, leftover)) shares[index] += 1;
	return shares;
};

/**
 * Split one shift's node (`staffNo`) into RN and LPN counts per room.
 *
 * LPN = min(lpnPerShift, staffNo); the rest are RNs. Each room first gets its census share of the
 * node. Each LPN then goes to the room with the most unfilled share that has an LPN row (ties:
 * larger census, then name), and the rest of the room's share are RNs. An LPN no room can take is
 * added to `problems` and its slot stays an RN, so the rooms still add up to `staffNo`.
 */
export const splitNodeAcrossRooms = (staffNo: number, rooms: readonly NodeRoom[], lpnPerShift: number, problems: NodeSplitProblem[]): RoomSplit[] => {
	if (rooms.length === 0) return [];
	const shares = roomShares(staffNo, rooms);
	const lpn = rooms.map(() => 0);
	const openShare = (index: number): number => shares[index] - lpn[index];
	const lpnCount = Math.min(lpnPerShift, staffNo);
	for (let placed = 0; placed < lpnCount; placed++) {
		const target = rooms
			.map((_, index) => index)
			.filter((index) => rooms[index].hasLpnRow && openShare(index) >= 1)
			.sort((a, b) => openShare(b) - openShare(a) || byCensusThenName(rooms[a], rooms[b]))
			.at(0);
		if (target === undefined) {
			const roomsKey = rooms.map((room) => `${room.name}/${room.hasLpnRow ? "lpn" : "no-lpn"}`).join("|");
			problems.push({ message: "Node split: no room with an LPN row can take the LPN share", details: { staff_no: staffNo, rooms: roomsKey } });
			break;
		}
		lpn[target] += 1;
	}
	return rooms.map((room, index) => ({ name: room.name, rn: openShare(index), lpn: lpn[index] }));
};

type NodeRole = "rn" | "lpn";

// A row whose only role is RN or LPN. Rows with more roles, "all", or other roles are not split by
// the node; they stay on the score/ladder path.
const nodeRoleOf = (location: DepartmentLocation): NodeRole | null => {
	if (location.roles.length !== 1) return null;
	if (location.roles[0] === NurseFullRole.RN) return "rn";
	return location.roles[0] === NurseFullRole.LPN ? "lpn" : null;
};

type RoomRows = { census: number; rows: Record<NodeRole, DepartmentLocation[]> };

/** Group one shift type's RN/LPN rows by room name, with the room's census. */
const groupRowsByRoom = (
	rows: readonly DepartmentLocation[],
	byDimensions: Record<string, number>,
	totalCensus: number,
	statsMode: StatsMode,
): Map<string, RoomRows> => {
	const rooms = new Map<string, RoomRows>();
	for (const row of rows) {
		const role = nodeRoleOf(row);
		if (role === null) continue;
		const name = row.location.trim();
		const room = rooms.get(name) ?? { census: 0, rows: { rn: [], lpn: [] } };
		// Same-named rows can map differently; take the widest, like the printout's census label.
		room.census = Math.max(room.census, resolveLocationCensus(row, byDimensions, totalCensus, statsMode, ALLOW_AVAILABILITY_FALLBACK));
		room.rows[role].push(row);
		rooms.set(name, room);
	}
	return rooms;
};

/** Put each room's RN/LPN count on its first row of that role (by id); other rows get 0. */
const assignRoomSplit = (room: RoomRows, split: RoomSplit, result: Map<string, number>, problems: NodeSplitProblem[]): void => {
	for (const role of ["rn", "lpn"] as const) {
		const [first, ...rest] = [...room.rows[role]].sort((a, b) => a.id.localeCompare(b.id));
		for (const row of rest) result.set(row.id, 0);
		if (first !== undefined) result.set(first.id, split[role]);
		else if (split[role] > 0) problems.push({ message: "Node split: room has a share but no row for the role", details: { room: split.name, role } });
	}
};

type NodeSplitArgs = {
	locations: readonly DepartmentLocation[];
	week: WeekData;
	dayForDate: Day;
	dayIndex: number;
	statsMode: StatsMode;
	lpnPerShift: number;
	problems: NodeSplitProblem[];
};

/**
 * Each RN/LPN row's share of its shift's node on one day, keyed by location id. Built from all
 * the department's locations active that day, so every view gets the same split.
 * The node ignores any census override: it is the shift's staffNo, not a census number.
 */
const nodeRequiredByLocationId = ({ locations, week, dayForDate, dayIndex, statsMode, lpnPerShift, problems }: NodeSplitArgs): Map<string, number> => {
	const weekday = dayForDate.date.setLocale("en").toFormat("ccc").toLowerCase();
	const byDimensions = byDimensionsOf(dayForDate.census_grouping);
	const totalCensus = getCensus(week, dayIndex);
	const activeRows = locations.filter((location) => location.days.includes(weekday) && nodeRoleOf(location) !== null);
	const result = new Map<string, number>();
	for (const shiftType of new Set(activeRows.map((row) => row.shift_type))) {
		const rooms = groupRowsByRoom(
			activeRows.filter((row) => row.shift_type === shiftType),
			byDimensions,
			totalCensus,
			statsMode,
		);
		// Found by date, not index. No Day for this shift → its rows need nobody (no ladder fallback).
		const shiftDay = week.weeklyShifts.find((shift) => shift.type === shiftType)?.days.find((day) => day.date.hasSame(dayForDate.date, "day"));
		const staffNo = shiftDay === undefined ? 0 : shiftDay.staffNo;
		const nodeRooms = [...rooms].map(([name, room]) => ({ name, census: room.census, hasLpnRow: room.rows.lpn.length > 0 }));
		for (const split of splitNodeAcrossRooms(staffNo, nodeRooms, lpnPerShift, problems)) {
			const room = rooms.get(split.name);
			if (room !== undefined) assignRoomSplit(room, split, result, problems);
		}
	}
	return result;
};

export type DemoRequiredDayArgs = {
	department: Pick<Department, "department_id" | "locations" | "statsMode">;
	week: WeekData | undefined;
	dayIndex: number;
	/** The caller's hospital-demo gate for the score path (unchanged per call site). */
	isRealHospitalDemo: boolean;
};

/** Every RN/LPN row at 0. Used when the day is not loaded: no shift Day means nobody is needed. */
const zeroNodeRows = (locations: readonly DepartmentLocation[]): Map<string, number> =>
	new Map(locations.filter((location) => nodeRoleOf(location) !== null).map((location) => [location.id, 0]));

/**
 * The one builder every required-staff view uses for a day: the node split when the department's
 * demo rules ask for it, the patient score on a real hospital demo, otherwise `null`.
 * On a node-split department a day that is not loaded gives every RN/LPN row 0, never the ladder.
 */
export const demoRequiredForDay = ({ department, week, dayIndex, isRealHospitalDemo }: DemoRequiredDayArgs): DemoRequiredInput => {
	const scoreByShiftType = scoreByShiftTypeForDay(week, dayIndex, isRealHospitalDemo);
	const nodeSplit = getDemoStaffingRules(department.department_id)?.nodeSplit;
	if (!nodeSplit) return scoreByShiftType === null ? null : { kind: "score", scoreByShiftType };
	const dayForDate = week?.weeklyShifts[0]?.days[dayIndex];
	const problems: NodeSplitProblem[] = [];
	const requiredByLocationId =
		week === undefined || dayForDate === undefined
			? zeroNodeRows(department.locations)
			: nodeRequiredByLocationId({
					locations: department.locations,
					week,
					dayForDate,
					dayIndex,
					statsMode: department.statsMode,
					lpnPerShift: nodeSplit.lpnPerShift,
					problems,
				});
	return { kind: "node", requiredByLocationId, scoreByShiftType, problems };
};
